/**
 * Permission engine. Combines the current mode with the model-supplied risk
 * (1–5) to decide whether a forwarded tool runs automatically, needs the user's
 * approval, or is blocked outright.
 */
import type { Level } from "./types.ts";

export type Decision = "allow" | "ask" | "deny";

export interface PermissionState {
  /** Auto-accept level 1–5: calls at risk ≤ level run automatically; riskier ones ask. */
  level: Level;
  /** Tool names the user chose to always allow this session. */
  alwaysAllow: Set<string>;
  /**
   * Risk levels the user granted this session ("allow level N"). Risk is a
   * GRADIENT, not distinct buckets: a grant at level N auto-accepts everything
   * at risk ≤ N — approving installs (3) obviously covers safe writes (2).
   * Kept as a Set (not a single ceiling) only for persisted-approval
   * compatibility; decide() always reads it as max().
   */
  allowRisk: Set<number>;
}

/** The effective auto-accept ceiling: base level raised by any risk grants. */
export function riskCeiling(state: PermissionState): number {
  let ceiling: number = state.level;
  for (const granted of state.allowRisk) {
    if (granted > ceiling) ceiling = granted;
  }
  return ceiling;
}

export function decide(
  state: PermissionState,
  tool: string,
  risk: number | null,
  hasPermissionRequest: boolean
): Decision {
  const effectiveRisk = typeof risk === "number" ? Math.min(5, Math.max(1, risk)) : hasPermissionRequest ? 3 : 1;

  // Standing tool approvals the user granted this session.
  if (state.alwaysAllow.has(tool)) return "allow";

  // One gradient: anything at or below the effective ceiling (base level,
  // raised by session risk grants) runs without asking.
  return effectiveRisk <= riskCeiling(state) ? "allow" : "ask";
}

/**
 * Catastrophic-command guard that applies regardless of mode. These are blocked
 * even in bypass mode — a coding agent should never be able to wipe the machine.
 */
const CATASTROPHIC_PATTERNS: RegExp[] = [
  /\brm\s+(-[a-z]*\s+)*-[a-z]*f[a-z]*\s+(-[a-z]*\s+)*(\/|~|\$HOME|\/\*|\.\s*$|\/\s*$)/i, // rm -rf / , rm -rf ~
  /\brm\s+-rf\s+--no-preserve-root/i,
  /:\(\)\s*\{\s*:\|:&\s*\}\s*;:/, // fork bomb
  /\bmkfs(\.\w+)?\b/i, // format filesystem
  /\bdd\b[^\n]*\bof=\/dev\/(sd|disk|nvme|hd)/i, // overwrite raw disk
  /\b(shutdown|reboot|halt|poweroff)\b/i,
  />\s*\/dev\/(sd|disk|nvme|hd)/i,
  /\bchmod\s+-R\s+(000|777)\s+\/(?:\s|$)/i,
];

export function isCatastrophic(command: string): boolean {
  return CATASTROPHIC_PATTERNS.some((re) => re.test(command));
}
